import type { IngestSessionMeta, ObsrviqEvent, ObsrviqInitConfig } from '@logclip/types';
import type { InstrumentCtx, ResolvedConfig, Teardown } from './context.js';
import { Transport } from './transport.js';
import { instrumentDom, takeFullSnapshot } from './recorder.js';
import { instrumentConsole } from './console.js';
import { instrumentNetwork } from './network.js';
import { instrumentWebSocket } from './websocket.js';
import { setCustomMaskKeys, markMaskConfigReady, redactUrl } from './mask.js';
import { instrumentErrors } from './errors.js';
import { instrumentVitals } from './vitals.js';
import { instrumentInteractions } from './interactions.js';
import { instrumentForms } from './forms.js';
import { instrumentScrollDepth } from './scroll.js';
import { instrumentPageviews } from './pageviews.js';
import { captureAcquisition } from './acquisition.js';
import { showConsent, showRecordingDot } from './ui.js';
import { clearSession, readSession, registerVisit, writeSession } from './storage.js';
import { sessionVerdict, watchIntervalMs, watchUserActivity } from './lifetime.js';
import { Clock, detectDevice, inEeaByTimezone, privacySignalsOptOut, sha256Hex, uuid } from './util.js';

const DEFAULTS = {
  maskAllInputs: true,
  // Both names: `.logclip-block` is the documented one; `.obsrviq-block` is in
  // customers' markup already and must keep working.
  blockSelector: '.logclip-block, .obsrviq-block',
  noPrivacy: false,
  captureNetworkBodies: false,
  redactHeaders: ['authorization', 'cookie', 'set-cookie'],
  tracePropagation: false,
  traceHosts: [] as string[],
  sampleRate: 1.0,
  recordCanvas: false,
  // The hosted ingest. The previous default, in.lumera.app, stopped resolving
  // after the move off that domain, so anyone who omitted `endpoint` was
  // silently sending nothing. Self-hosters pass their own.
  endpoint: 'https://app.logclip.com',
  flushIntervalMs: 5000,
  continueSession: false,
  sessionTimeoutMs: 30 * 60 * 1000,
  // A session ends after this long with no USER INPUT — see lifetime.ts. The
  // same 30 minutes the rest of the market uses, so a session count of ours
  // means what a session count of theirs means.
  idleTimeoutMs: 30 * 60 * 1000,
  // Backstop for continuous use; 24h matches the native mobile SDKs and keeps
  // `t` (and so sessions.duration_ms, an int4) comfortably bounded.
  maxSessionMs: 24 * 60 * 60 * 1000,
  // 'auto' → required for EU/EEA visitors, not elsewhere. See inEeaByTimezone().
  // `true` forces the gate everywhere; `false` disables it everywhere and is the
  // caller taking responsibility for their own lawful basis.
  requireConsent: 'auto' as boolean | 'auto',
  askPermission: false,
  showRecording: false,
  autoStart: true,
  maxArgBytes: 8 * 1024,
};

export interface Diagnostics {
  recording: boolean;
  sessionId: string | null;
  flushes: number;
  bytesSent: number;
  eventsSent: number;
  eventsQueued: number;
}

class ObsrviqClient {
  private config: ResolvedConfig | null = null;
  private clock: Clock | null = null;
  private transport: Transport | null = null;
  private teardowns: Teardown[] = [];
  private meta: IngestSessionMeta | null = null;
  /** The page-load visit's traffic source, held until the site's config allows it. */
  private landingAcquisition: IngestSessionMeta['acquisition'] | null = null;
  private landingSessionId: string | null = null;
  private recording = false;
  private consent: boolean | null = null;
  private sampledIn = true;
  /** Manual-control intent. In autoStart:false mode, recording only begins once
   *  the host calls startRecording() (which sets this); stopRecording() clears it. */
  private manualStartRequested = false;
  /** Last APP activity (mutation / nav / network), ms since session start. Feeds
   *  dead-click detection ONLY — it is not a liveness signal, because a polling
   *  app updates it forever with nobody at the keyboard. See lastUserActivityAt. */
  private lastActivity = 0;
  /** Last USER input (epoch ms) — the signal the idle end is judged on. */
  private lastUserActivityAt = 0;
  private lifetimeTimer: ReturnType<typeof setInterval> | null = null;
  private activityTeardown: Teardown | null = null;
  private onVisible: (() => void) | null = null;
  /** Set between an idle end and the user's next input: no session is open, and
   *  the next input starts a fresh one. Distinct from `!recording`, which also
   *  covers consent gates and an explicit stopRecording(). */
  private dormant = false;
  /** The userId this session is attributed to (for the shared-device split). */
  private currentUserId: string | undefined;
  /** Next batch seq — mirrored from the transport so we can persist continuation. */
  private nextSeq = 0;
  /** Open task spans keyed by task name → the span id + start time, so endTask()
   *  can pair with its startTask(). Re-starting the same name supersedes the
   *  earlier open span (the player pairs by spanId, so this only affects which
   *  start an unqualified end() resolves to). */
  private openTasks = new Map<string, { spanId: string; startT: number }>();
  private consentUnmount: (() => void) | null = null;
  private recDotUnmount: (() => void) | null = null;
  private diag: Diagnostics = {
    recording: false,
    sessionId: null,
    flushes: 0,
    bytesSent: 0,
    eventsSent: 0,
    eventsQueued: 0,
  };

  /** The current session id (null until init). */
  get sessionId(): string | null {
    return this.diag.sessionId;
  }

  init(config: ObsrviqInitConfig): void {
    if (this.config) return; // idempotent
    if (!config.siteKey) {
      console.warn('[obsrviq] init() called without siteKey — recording disabled.');
      return;
    }
    this.config = { ...DEFAULTS, ...stripUndefined(config) } as ResolvedConfig;
    // No-privacy: record input values verbatim by unmasking inputs — but only if
    // the host didn't set maskAllInputs explicitly (their choice always wins).
    // Page text is already verbatim unless maskTextSelector is set. Passwords stay
    // masked via the recorder's maskInputOptions floor.
    if (this.config.noPrivacy && config.maskAllInputs === undefined) {
      this.config.maskAllInputs = false;
    }
    // Resolve 'auto' before anything reads it as a boolean.
    if (this.config.requireConsent === 'auto') {
      this.config.requireConsent = inEeaByTimezone();
    }
    // askPermission = "show a built-in consent prompt"; it implies a consent gate.
    if (this.config.askPermission) this.config.requireConsent = true;

    // Custom masking keys: apply any static ones NOW, then fetch the per-site list
    // (GET /v1/config) and merge. Fired before the network instrumenter is installed
    // so this request is never self-recorded; the transport also re-masks custom
    // keys at send time so nothing captured before the fetch resolves can leak.
    const staticMaskKeys = Array.isArray(config.maskKeys) ? config.maskKeys : [];
    setCustomMaskKeys(staticMaskKeys);
    void this.loadRemoteMaskKeys(staticMaskKeys);

    this.startSession();

    // Init-time identity (rarely known this early — usually set later via identify()).
    if (config.userId != null) this.applyUserId(config.userId);
    if (config.metadata && this.meta) this.meta.metadata = { ...config.metadata };

    // GPC / DNT always wins → no recording, no UI (R-1).
    if (privacySignalsOptOut()) {
      this.consent = false;
      return;
    }

    if (this.config.requireConsent) {
      // Built-in prompt, if asked; otherwise the host calls setConsent(true) itself.
      if (this.config.askPermission) {
        this.consentUnmount = showConsent(
          this.config.blockSelector,
          this.config.consentText,
          () => this.setConsent(true),
          () => this.setConsent(false),
        );
      }
    } else {
      this.maybeStart();
    }
  }

  /** Fetch the per-site masking config (GET /v1/config) and merge its keys with any
   *  static ones. Best-effort: on failure the built-in masking still applies. */
  private async loadRemoteMaskKeys(staticKeys: string[]): Promise<void> {
    const cfg = this.config;
    if (!cfg || typeof fetch === 'undefined') {
      markMaskConfigReady();
      return;
    }
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 2000);
      try {
        const url = `${cfg.endpoint.replace(/\/$/, '')}/v1/config?key=${encodeURIComponent(cfg.siteKey)}`;
        const res = await fetch(url, { credentials: 'omit', mode: 'cors', signal: ctrl.signal });
        if (res.ok) {
          const data = (await res.json()) as { maskKeys?: unknown; trafficSources?: unknown };
          const remote = Array.isArray(data.maskKeys)
            ? data.maskKeys.filter((k): k is string => typeof k === 'string')
            : [];
          setCustomMaskKeys([...staticKeys, ...remote]);
          // Traffic sources are opt-in per site: attach the page-load visit's
          // source only when the site turned it on AND the embed didn't opt out.
          // Still the page-load session? (A split may already have replaced it.)
          if (
            data.trafficSources === true &&
            cfg.trafficSources !== false &&
            this.landingAcquisition &&
            this.meta &&
            this.diag.sessionId === this.landingSessionId
          ) {
            this.meta.acquisition = this.landingAcquisition;
          }
        }
      } finally {
        clearTimeout(timer);
      }
    } catch {
      /* offline / blocked / timeout / bad JSON — built-in masking still protects. */
    } finally {
      // Always release the transport's hold — a failed/slow fetch must never block
      // sending forever (built-in + static-key masking still applies).
      markMaskConfigReady();
    }
  }

  /** (Re)create the session id, clock, meta, and transport. Used by init() and
   *  reset(). Does not start recording — callers decide via maybeStart().
   *  @param seedInitUser seed identity from the init-time `cfg.userId`. True for
   *  the initial init() session; FALSE for reset() (logout) — otherwise the fresh
   *  post-logout session would be re-attributed to the user who just logged out. */
  private startSession(seedInitUser = true): void {
    const cfg = this.config!;
    // Session continuation (opt-in): resume the same session for a same-device
    // return within the inactivity window — but NOT when a different known user is
    // at the device (the shared-device split). A resumed session keeps recording
    // regardless of sampling (it was already sampled in).
    const now = Date.now();
    let resume: ReturnType<typeof readSession> = null;
    if (cfg.continueSession) {
      const rec = readSession();
      if (
        rec &&
        now - rec.lastActivity < cfg.sessionTimeoutMs &&
        !(cfg.userId != null && rec.userId != null && rec.userId !== String(cfg.userId))
      ) {
        resume = rec;
      }
    }
    const sessionId = resume ? resume.id : uuid();
    this.clock = resume ? new Clock(resume.startedAt, resume.lastT) : new Clock();
    this.currentUserId =
      resume?.userId ?? (seedInitUser && cfg.userId != null ? String(cfg.userId) : undefined);
    this.nextSeq = resume ? resume.seq : 0;
    this.diag.sessionId = sessionId;
    this.meta = {
      startedAt: this.clock.startedAt,
      entryUrl: redactUrl(location.href),
      device: detectDevice(),
    };
    // Where the visit came from — read now (SPA navigations rewrite location
    // later), but only for the session this page load starts: a resumed session
    // (continueSession) keeps the source it already has, and a split/reset
    // session within an open tab wasn't "arrived at" from anywhere. It is held
    // here and attached to meta only once GET /v1/config says the site opted in
    // (loadRemoteMaskKeys) — off by default, so nothing is sent until then.
    if (seedInitUser && !resume && cfg.trafficSources !== false) {
      this.landingAcquisition = captureAcquisition(location.href, document.referrer);
      this.landingSessionId = sessionId;
    }
    // Sampling decision for the whole session — a resumed one stays in.
    this.sampledIn = resume ? true : Math.random() < (cfg.sampleRate ?? 1);
    this.transport = new Transport({
      endpoint: cfg.endpoint,
      siteKey: cfg.siteKey,
      sessionId,
      getMeta: () => this.meta ?? undefined,
      beforeSend: cfg.beforeSend,
      flushIntervalMs: cfg.flushIntervalMs,
      startSeq: this.nextSeq,
      onSeqDispatch: (next) => {
        this.nextSeq = next;
        this.persistSession();
      },
      // Backpressure dropped DOM deltas → re-anchor the rrweb stream immediately
      // so replay stays coherent (a delta stream with a hole is corrupt until
      // the next 30s checkout).
      onDomDrop: () => takeFullSnapshot(),
      onFlush: ({ bytes, events, ok }) => {
        if (ok) {
          this.diag.flushes++;
          this.diag.bytesSent += bytes;
          this.diag.eventsSent += events;
        }
      },
    });
    this.recording = false;
    this.diag.recording = false;
    // A new session is by definition not idle yet — whatever opened it (init, a
    // reset, or the input that woke a dormant client) counts as the first
    // activity. Set here rather than in startLifetimeWatch() so every path that
    // mints a session gets it, not just the first.
    this.lastUserActivityAt = Date.now();
  }

  /** Persist the session-continuation record (no-op unless continueSession). Only
   *  ever writes for a RECORDING session — a sampled-out or pre-consent session must
   *  not leave a record that a later page would resume (and force back into
   *  recording), and identify() can fire before recording starts. */
  private persistSession(): void {
    if (!this.config?.continueSession || !this.recording || !this.clock || !this.diag.sessionId) return;
    writeSession({
      id: this.diag.sessionId,
      startedAt: this.clock.startedAt,
      lastActivity: Date.now(),
      seq: this.nextSeq,
      lastT: this.clock.now(),
      userId: this.currentUserId,
    });
  }

  private maybeStart(): void {
    if (this.recording || !this.config || !this.clock || !this.transport) return;
    // Manual mode: hold off until the host explicitly calls startRecording().
    if (this.config.autoStart === false && !this.manualStartRequested) return;
    if (this.config.requireConsent && this.consent !== true) return;
    if (this.consent === false) return;
    if (!this.sampledIn) return;

    // Returning-visitor identity: now that consent is granted and we're about to
    // record, read-or-create the persistent (anonymous) visitor record and stamp
    // it onto the session meta. Guarded internally — never blocks recording.
    if (this.meta && this.meta.visitorId == null) {
      const visit = registerVisit(Date.now());
      this.meta.visitorId = visit.id;
      this.meta.visitCount = visit.visits;
      this.meta.firstSeenAt = visit.firstSeenAt;
    }

    const ctx: InstrumentCtx = {
      clock: this.clock,
      sessionId: this.diag.sessionId!,
      config: this.config,
      emit: (e: ObsrviqEvent) => {
        this.transport!.enqueue(e);
        this.diag.eventsQueued++;
      },
      markActivity: () => {
        this.lastActivity = this.clock!.now();
      },
      lastActivityT: () => this.lastActivity,
    };

    this.transport.start();
    // Order matters slightly: network/console wrappers should be installed
    // before app code runs more requests; DOM last so its snapshot is current.
    this.teardowns.push(instrumentConsole(ctx));
    this.teardowns.push(instrumentNetwork(ctx));
    this.teardowns.push(instrumentWebSocket(ctx));
    this.teardowns.push(instrumentErrors(ctx));
    this.teardowns.push(instrumentVitals(ctx));
    this.teardowns.push(instrumentInteractions(ctx));
    this.teardowns.push(instrumentForms(ctx));
    this.teardowns.push(instrumentScrollDepth(ctx));
    this.teardowns.push(instrumentPageviews(ctx));
    this.teardowns.push(instrumentDom(ctx));

    this.recording = true;
    this.diag.recording = true;
    this.dormant = false;

    // Now that we're actually recording, anchor the continuation record so a next
    // page can resume this (and ONLY a recording) session, even before any flush.
    this.persistSession();

    // Nothing to end before there is something recording, so the lifetime watch
    // starts here rather than in init().
    this.startLifetimeWatch();

    // Transparency: optional visible "recording" indicator.
    if (this.config.showRecording && !this.recDotUnmount) {
      this.recDotUnmount = showRecordingDot(this.config.blockSelector);
    }
  }

  // ── Session lifetime (see lifetime.ts) ────────────────────────────────────

  /** Begin watching for the idle / max-length ends. Idempotent, and deliberately
   *  NOT undone by teardown(): the idle end tears the instrumenters down but the
   *  input listeners have to survive it, because they are what wakes the client
   *  back up. Only an explicit stop / consent withdrawal removes them. */
  private startLifetimeWatch(): void {
    const cfg = this.config;
    if (!cfg || this.activityTeardown) return;
    if (cfg.idleTimeoutMs <= 0 && cfg.maxSessionMs <= 0) return; // both ends disabled

    this.activityTeardown = watchUserActivity(() => this.onUserActivity());
    this.lifetimeTimer = setInterval(
      () => this.checkLifetime(),
      watchIntervalMs(cfg.idleTimeoutMs),
    );
    // A background tab's interval is throttled and a frozen one doesn't tick at
    // all, so re-check the moment it comes back rather than waiting a full tick.
    if (typeof document !== 'undefined') {
      this.onVisible = () => {
        if (!document.hidden) this.checkLifetime();
      };
      document.addEventListener('visibilitychange', this.onVisible);
    }
  }

  private stopLifetimeWatch(): void {
    this.activityTeardown?.();
    this.activityTeardown = null;
    if (this.lifetimeTimer != null) {
      clearInterval(this.lifetimeTimer);
      this.lifetimeTimer = null;
    }
    if (this.onVisible && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisible);
    }
    this.onVisible = null;
    this.dormant = false;
  }

  private onUserActivity(): void {
    this.lastUserActivityAt = Date.now();
    if (this.dormant) this.wake();
  }

  /** Test the verdict. A no-op unless a session is actually open — an explicit
   *  stopRecording() or a withheld consent must not be "ended". */
  private checkLifetime(): void {
    if (!this.recording || !this.config || !this.clock) return;
    const verdict = sessionVerdict({
      now: Date.now(),
      lastUserActivityAt: this.lastUserActivityAt,
      sessionT: this.clock.now(),
      idleTimeoutMs: this.config.idleTimeoutMs,
      maxSessionMs: this.config.maxSessionMs,
    });
    if (verdict === 'idle') this.endForIdle();
    else if (verdict === 'expired') this.rotate('maxLength');
  }

  /** End the session and go dormant. No new session is opened here — opening one
   *  for someone who has walked away would produce an empty row and restart the
   *  very clock we are trying to stop. wake() opens the next one, on their next
   *  input. */
  private endForIdle(): void {
    this.markSessionEnd('idle');
    this.teardown(); // flushes the final batch
    // Nothing may resume a session we just ended: with continueSession on, the
    // resume window and the idle window are both 30 minutes by default, so the
    // next page load would otherwise re-open it.
    clearSession();
    this.dormant = true;
  }

  /** End the current session and immediately open the next one. For the
   *  max-length end, where the user is mid-flow and recording must not pause. */
  private rotate(reason: 'maxLength'): void {
    this.markSessionEnd(reason);
    this.teardown();
    clearSession();
    this.newSessionKeepingIdentity();
    this.maybeStart();
  }

  private wake(): void {
    this.dormant = false;
    this.newSessionKeepingIdentity();
    this.maybeStart();
  }

  /** A final marker so a session's end is legible in its own timeline — support
   *  can tell "ended by inactivity" from "closed the tab" (which leaves none).
   *  Underscore-prefixed like `_click` and `_tag`; the server treats an unknown
   *  custom event as a plain timeline event, so this needs no ingest change. */
  private markSessionEnd(reason: 'idle' | 'maxLength'): void {
    // Must be enqueued BEFORE teardown(): transport.stop() sets `stopped`, after
    // which enqueue() silently drops.
    this.track('_session_end', {
      reason,
      idleMs: Math.max(0, Date.now() - this.lastUserActivityAt),
    });
  }

  /** Start a fresh session carrying the identity and metadata forward. The
   *  person did not change — only the session did — so re-stamping is what keeps
   *  the new session searchable by the same user. (reset() is the opposite case:
   *  a logout, which must NOT carry identity across.) */
  private newSessionKeepingIdentity(): void {
    const userId = this.currentUserId;
    const metadata = this.meta?.metadata;
    // Open spans are anchored to the old clock; carrying them over would emit
    // ends whose startT belongs to a different timeline.
    this.openTasks.clear();
    // clearSession() has already run, so this can never resume the session we
    // just ended; `false` stops the init-time userId being re-seeded on top of
    // an identify() that has since superseded it.
    this.startSession(false);
    if (userId != null) {
      this.currentUserId = userId;
      this.applyUserId(userId);
    }
    if (metadata && this.meta) this.meta.metadata = { ...metadata };
  }

  /** Associate the session with an application user. Stores the RAW userId (for
   *  search + display) AND a SHA-256 identityHash, merges any traits into metadata,
   *  and force-flushes so the live session is retro-stamped promptly. Safe to call
   *  before recording starts (values ride the first batch) and after login. */
  identify(userId: string, traits?: Record<string, unknown>): void {
    if (!this.meta) return;
    const uid = String(userId);
    // Shared-device split: if this session is already attributed to a DIFFERENT
    // user (continueSession only), rotate to a fresh session so two people never
    // merge into one. reset() clears the continuation record so it can't resume
    // the prior user. (Same user, or first identify on the session → no split.)
    if (this.config?.continueSession && this.currentUserId != null && this.currentUserId !== uid) {
      this.reset();
    }
    this.currentUserId = uid;
    this.applyUserId(uid);
    if (traits) this.mergeMetadata(traits);
    // track() emits an event only while recording — which also guarantees the
    // next flush is non-empty so the updated meta actually ships (empty buffers
    // are skipped by transport.flush()).
    this.track('identify', { hasTraits: !!traits });
    void this.flush();
    // Stamp the userId onto the continuation record so the next page knows it.
    this.persistSession();
  }

  /** Merge custom properties onto the session at any time (searchable server-side). */
  setMetadata(props: Record<string, unknown>): void {
    if (!this.meta) return;
    this.mergeMetadata(props);
    this.track('metadata', { keys: Object.keys(props) });
    void this.flush();
  }

  /** Forget the current identity (e.g. on logout) and rotate to a fresh anonymous
   *  session, so post-logout activity isn't attributed to the prior user. (Identity
   *  can't be cleared on the existing row — the server merge only fills nulls — so
   *  we start a new session instead.) */
  reset(): void {
    if (!this.config) return;
    const wasRecording = this.recording;
    if (this.recording) this.teardown(); // flushes the old session's final batch
    // An explicit reset (logout / user switch) must NEVER resume the prior
    // session — drop the continuation record so startSession() begins fresh.
    clearSession();
    this.currentUserId = undefined;
    // Fresh session must stay anonymous — do NOT re-seed the init-time userId, or a
    // post-logout session would be re-attributed to the user who just logged out.
    this.startSession(false);
    if (wasRecording && this.consent !== false && !privacySignalsOptOut()) this.maybeStart();
  }

  /** Set the raw userId on meta and compute its hash (async). */
  private applyUserId(userId: string): void {
    const raw = String(userId);
    if (this.meta) this.meta.userId = raw;
    void sha256Hex(raw).then((hash) => {
      if (this.meta && this.meta.userId === raw) this.meta.identityHash = hash;
    });
  }

  private mergeMetadata(props: Record<string, unknown>): void {
    if (!this.meta) return;
    this.meta.metadata = { ...(this.meta.metadata ?? {}), ...props };
  }

  /** Emit a custom event (e.g. conversions). */
  track(name: string, props?: Record<string, unknown>): void {
    if (!this.recording || !this.clock || !this.transport) return;
    this.transport.enqueue({
      id: uuid(),
      sessionId: this.diag.sessionId!,
      type: 'custom',
      t: this.clock.now(),
      ts: this.clock.wall(),
      name,
      props,
    });
    this.diag.eventsQueued++;
  }

  /** Mark a conversion. Conversion is client-defined, so the NAME matters —
   *  "checkout", "signup", "trial_started" — and each is denormalised onto the
   *  session for per-client filtering. Sets props.conversion so the legacy
   *  'conversion' flag still lights up. Sugar over track(name, {conversion:true}). */
  conversion(name: string, props?: Record<string, unknown>): void {
    const clean = String(name).trim();
    if (!clean) return;
    this.track(clean, { ...props, conversion: true });
  }

  /** Attach one or more free-form, searchable labels to the current session.
   *  Tags are session-level (deduped server-side) — use them to slice sessions
   *  however a client likes ("vip", "ab_variant_b", "mobile_app"). */
  tag(...names: string[]): void {
    const clean = names.map((n) => String(n).trim()).filter(Boolean);
    if (!clean.length) return;
    this.track('_tag', { tags: clean });
    void this.flush();
  }

  /** Begin a named sub-event span. Pairs with end() / endTask(name) to produce a
   *  searchable, duration-bearing span on the session timeline. Returns a handle
   *  so callers can `const t = LogClip.startTask('checkout'); …; t.end()`. */
  startTask(name: string, props?: Record<string, unknown>): { end: (endProps?: Record<string, unknown>) => void } {
    const clean = String(name).trim();
    const spanId = uuid();
    const startT = this.clock ? this.clock.now() : 0;
    if (clean) {
      this.openTasks.set(clean, { spanId, startT });
      this.track(`${clean}:started`, { ...props, spanId, task: clean });
    }
    return { end: (endProps?: Record<string, unknown>) => this.endTask(clean, endProps) };
  }

  /** Close a named sub-event span opened by startTask(name). Safe to call even
   *  if the start was never seen (renders as an orphan end on the timeline). */
  endTask(name: string, props?: Record<string, unknown>): void {
    const clean = String(name).trim();
    if (!clean) return;
    const open = this.openTasks.get(clean);
    this.openTasks.delete(clean);
    const spanId = open?.spanId ?? uuid();
    this.track(`${clean}:ended`, {
      ...props,
      spanId,
      task: clean,
      ...(open ? { startT: open.startT } : {}),
    });
  }

  /** Gate recording (PRIV-2). true starts; false stops + flushes. Dismisses the
   *  built-in consent prompt if one is showing. */
  setConsent(granted: boolean): void {
    this.consent = granted;
    this.consentUnmount?.();
    this.consentUnmount = null;
    if (granted) this.maybeStart();
    else {
      // Consent withdrawn: stop listening entirely. Leaving the input listeners
      // installed would keep watching someone who just said no.
      this.stopLifetimeWatch();
      if (this.recording) this.teardown();
    }
  }

  /** Begin recording now. The trigger for `autoStart: false` (manual) mode —
   *  nothing records until this is called. Idempotent: a no-op while already
   *  recording. Consent, GPC/DNT and sampling still apply (so a consent-gated or
   *  sampled-out session won't start just because this was called). After a
   *  stopRecording(), this resumes the SAME session with the idle gap shown. */
  startRecording(): void {
    this.manualStartRequested = true;
    this.maybeStart();
  }

  /** Stop recording and flush the final batch. Recording stays off until
   *  startRecording() is called again. (Friendlier name for stop(), and it also
   *  clears the manual-start intent so recording won't silently resume.) */
  stopRecording(): void {
    this.manualStartRequested = false;
    // The host is driving the lifecycle now; an idle end firing underneath them
    // would silently rotate the session they intend to resume.
    this.stopLifetimeWatch();
    if (this.recording) this.teardown();
  }

  /** Hard stop + flush. */
  stop(): void {
    this.stopLifetimeWatch();
    this.teardown();
  }

  /** Force a flush now (returns when send is attempted). */
  flush(): Promise<boolean> {
    return this.transport ? this.transport.flush('manual') : Promise.resolve(true);
  }

  getDiagnostics(): Diagnostics {
    return { ...this.diag };
  }

  private teardown(): void {
    for (const t of this.teardowns.splice(0)) {
      try {
        t();
      } catch {
        /* ignore */
      }
    }
    this.recDotUnmount?.();
    this.recDotUnmount = null;
    this.transport?.stop();
    this.recording = false;
    this.diag.recording = false;
  }
}

function stripUndefined<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

export const LogClip = new ObsrviqClient();
/** Pre-rebrand named export. Kept so `import { Obsrviq } from '@logclip/tracker'`
 *  in customer code keeps resolving to the same singleton after the rename. */
export const Obsrviq = LogClip;
export type { ObsrviqInitConfig } from '@logclip/types';
export default LogClip;
